import { twMerge } from 'tailwind-merge';

import type { EjsBlockData } from '../../editor/editorjs/@types';
import { ReactComponent as IconForm } from '../../icons/icon-form.svg';
import { ReactComponent as IconImage } from '../../icons/icon-image.inline.svg';
import { ReactComponent as IconLink } from '../../icons/icon-link.svg';
import { ReactComponent as IconQuote } from '../../icons/icon-quote.svg';
import { ReactComponent as IconUndefined } from '../../icons/icon-undefined.svg';
import { ReactComponent as IconVideo } from '../../icons/icon-video.svg';
import { getSortedEjsEmbedsBlocks } from '../../utils/editor';
import type { MessageParsedEmbeds } from '../ThreadMessage/threadTypes';

const embedLikeBlocks = ['linkTool', 'quote', 'image', 'embed', 'googleForm'] as const;

const isEmbedLikeBlock = (
  block: EjsBlockData,
): block is Extract<EjsBlockData, { type: (typeof embedLikeBlocks)[number] }> =>
  embedLikeBlocks.includes(block.type as (typeof embedLikeBlocks)[number]);

type EmbedsOverviewProps = {
  messageEmbeds: MessageParsedEmbeds;
  className?: string;
  avoidSoleEmbedOverview?: boolean;
};

const getEmbedIcon = (embedType: string) => {
  switch (embedType) {
    case 'linkTool':
      return IconLink;
    case 'quote':
      return IconQuote;
    case 'image':
      return IconImage;
    case 'embed':
      return IconVideo;
    case 'googleForm':
      return IconForm;
    default:
      return IconUndefined;
  }
};

/**
 * Displays icons of embeds types, and a counter for each type.
 */
export const EmbedsOverview = ({
  messageEmbeds,
  className,
  avoidSoleEmbedOverview,
}: EmbedsOverviewProps) => {
  const embededBlocks = messageEmbeds?.blocks!.filter(isEmbedLikeBlock);
  if ((avoidSoleEmbedOverview && embededBlocks.length === 1) || embededBlocks.length === 0)
    return null;

  const sortedEmbeds = getSortedEjsEmbedsBlocks(embededBlocks);

  return (
    <div
      className={twMerge(
        'EmbedsOverview my-2 flex justify-center items-center gap-4 text-center',
        className,
      )}
    >
      {sortedEmbeds &&
        Object.entries(sortedEmbeds).map(([type, blocks], index) => (
          <EmbedOverviewItem key={index} type={type} blocks={blocks} />
        ))}
    </div>
  );
};

const EmbedOverviewItem = ({ type, blocks }: { type: string; blocks: EjsBlockData[] }) => {
  const EmbedIcon = getEmbedIcon(type);
  return (
    <div className="EmbedOverviewItem flex border border-gray-250 rounded-md overflow-hidden">
      <div className="icon-container flex items-center bg-primary text-white px-2">
        <EmbedIcon className="w-4 h-4" />
      </div>
      <div className="counter-container px-2">{blocks.length}</div>
    </div>
  );
};
